/* One queue for cart mutations and their readbacks across PDP, quick add and bulk order. */
if (!window.MidiCart) {
  let tail = Promise.resolve();
  let pending = 0;
  const disabledStates = new WeakMap();
  window.MidiCart = {
    get busy() { return pending > 0; },
    async acquire() {
      pending += 1;
      this.lockViews();
      const previous = tail;
      let release;
      tail = new Promise(resolve => { release = resolve; });
      await previous.catch(() => {});
      let released = false;
      return () => {
        if (released) return;
        released = true;
        pending -= 1;
        this.lockViews();
        release();
      };
    },
    async requestJSON(url, options = {}) {
      // Bound both headers and body consumption. An aborted write has an unknown
      // server outcome: callers may reconcile with reads, never repeat the POST.
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        const result = await response.json();
        return { response, result };
      } finally {
        clearTimeout(timeout);
      }
    },
    async read(cartUrl) {
      const { response, result: cart } = await this.requestJSON(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Cart unavailable');
      if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) throw new Error('Invalid cart response');
      return cart;
    },
    publish(cart, options = {}) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, ...options } }));
    },
    views() { return [...document.querySelectorAll('[data-cart-section]')]; },
    sectionRequest() {
      const sections = [...new Set(this.views().map(view => view.dataset.cartSection))];
      return sections.length ? { sections, sections_url: window.location.pathname } : {};
    },
    lockViews() {
      const active = document.activeElement;
      if (this.busy && !this.focusedControl && active?.matches('[data-cart-quantity], [data-cart-remove]')) {
        this.focusedControl = { view: active.closest('[data-cart-section]')?.dataset.cartSection, key: active.dataset.lineKey, quantity: active.matches('[data-cart-quantity]') };
      }
      this.views().forEach(view => {
        const disabled = this.busy || view.hasAttribute('data-cart-stale');
        view.querySelector('[data-cart-content]')?.setAttribute('aria-busy', String(this.busy));
        view.querySelectorAll('[data-cart-content] input, [data-cart-content] button').forEach(control => {
          if (disabled) {
            if (!disabledStates.has(control)) disabledStates.set(control, control.disabled);
            control.disabled = true;
          } else if (disabledStates.has(control)) {
            control.disabled = disabledStates.get(control);
            disabledStates.delete(control);
          }
        });
        view.querySelectorAll('[data-cart-remove]').forEach(link => {
          if (disabled) link.setAttribute('aria-disabled', 'true');
          else link.removeAttribute('aria-disabled');
        });
      });
      if (!this.busy && this.focusedControl) {
        const saved = this.focusedControl;
        this.focusedControl = null;
        const view = this.views().find(node => node.dataset.cartSection === saved.view);
        const modal = document.querySelector('dialog[open]');
        if (view && (!modal || view.contains(modal)) && (document.activeElement === document.body || view.contains(document.activeElement))) {
          const selector = saved.quantity ? '[data-cart-quantity]' : '[data-cart-remove]';
          const target = [...view.querySelectorAll(selector)].find(control => control.dataset.lineKey === saved.key);
          const fallback = view.querySelector('[data-cart-close], h1');
          if (fallback?.tagName === 'H1') fallback.tabIndex = -1;
          (target && !target.disabled ? target : fallback)?.focus();
        }
      }
    },
    markStale(stale) {
      this.views().forEach(view => view.toggleAttribute('data-cart-stale', stale));
      this.lockViews();
    },
    async fetchSections(cartUrl) {
      const { sections } = this.sectionRequest();
      if (!sections?.length) return {};
      const url = new URL(cartUrl, window.location.origin);
      url.searchParams.set('sections', sections.join(','));
      // On /cart, Accept: application/json selects cart data instead of section HTML.
      // Keep the browser's default Accept header; the section response is still JSON.
      const { response, result } = await this.requestJSON(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart display unavailable');
      return result;
    },
    renderSections(sections) {
      // Validate every fragment before updating either cart view.
      const replacements = this.views().map(view => {
        const html = sections?.[view.dataset.cartSection];
        const content = typeof html === 'string' && new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-content]');
        if (!content) throw new Error('Cart display unavailable');
        return [view.querySelector('[data-cart-content]'), content];
      });
      replacements.forEach(([target, content]) => target.replaceChildren(...content.childNodes));
      this.markStale(false);
    },
    // Caller owns the queue. Never acquire it again while completing a mutation.
    async sync(cartUrl, sections, cart) {
      if (!Array.isArray(cart?.items) || !Number.isInteger(cart?.item_count)) cart = await this.read(cartUrl);
      // Publish confirmed data immediately; this caller owns the pending view
      // refresh so subscribers must not enqueue another one.
      this.publish(cart, { renderingSections: true });
      try {
        if (!sections || this.views().some(view => typeof sections[view.dataset.cartSection] !== 'string')) sections = await this.fetchSections(cartUrl);
        this.renderSections(sections);
        document.dispatchEvent(new CustomEvent('cart:rendered', { detail: { cart } }));
      } catch (error) {
        this.markStale(true);
        throw error;
      }
      return cart;
    },
    async afterAdd({ cartUrl, mode, result, trigger }) {
      if (mode === 'cart') { window.location.assign(cartUrl); return true; }
      const drawer = document.querySelector('midi-cart-drawer');
      try {
        await this.sync(cartUrl, result?.sections);
        drawer?.feedback?.('');
      } catch (_) {
        // The add already succeeded. Display recovery must never become a second add.
        this.markStale(true);
        drawer?.feedback?.(drawer.dataset.refreshError || drawer.dataset.error, true);
      }
      if (mode === 'drawer' && !drawer?.open?.(trigger, { refresh: false })) {
        window.location.assign(cartUrl);
        return true;
      }
      return false;
    },
    async refresh(cartUrl) {
      const release = await this.acquire();
      try { const cart = await this.read(cartUrl); this.publish(cart); return cart; }
      finally { release(); }
    }
  };
  document.addEventListener('submit', event => {
    const view = event.target.closest('[data-cart-section]');
    if (view && (window.MidiCart.busy || view.hasAttribute('data-cart-stale'))) event.preventDefault();
  });
  document.addEventListener('click', event => {
    if (event.target.closest('[data-cart-remove][aria-disabled="true"]')) event.preventDefault();
  });
}
if (!customElements.get('midi-cart-count')) {
  customElements.define('midi-cart-count', class extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      document.addEventListener('cart:updated', event => {
        const count = event.detail?.cart?.item_count;
        if (!Number.isInteger(count)) return;
        this.textContent = count;
        const label = count === 1 ? this.dataset.labelOne : this.dataset.labelOther;
        if (label) this.closest('a')?.setAttribute('aria-label', label.replace('[count]', count));
      }, { signal: this.listeners.signal });
    }
    disconnectedCallback() { this.listeners?.abort(); }
  });
}
